import type { Model } from '../../../data/models'
import { SAFETY_FILTER, SPEED } from './constants'

export interface ModelConfig {
	isVideo: boolean
	requiresImage: boolean
	supportsImageInput: boolean
	requiresVideo: boolean
	requiresTwoImages: boolean
	supportsMultipleImages: boolean
	showImageUpload: boolean
	maxImages: number
	paramHints: Record<string, { label?: string; description?: string }>
	hint: (key: string) => string | undefined
	hintLabel: (key: string, fallback: string) => string
}

/** Нужен ли модели текст.
 *
 * Есть модели-операции, которые промпт не читают ВОВСЕ: face-swap просто
 * переносит лицо, а текст отбрасывает (на бэке `_face_swap` делает
 * `data.clear()`). Требовать от человека текст, который гарантированно ничего
 * не изменит, — обман: он его пишет, платит и не понимает, почему не подействовал.
 * Признак приходит из `ui_metadata.noPrompt` модели, руками в коде не задаётся.
 *
 * isParametricAudio — рантайм-состояние (операция над треком), поэтому передаётся
 * снаружи, а не выводится из модели. Правило собрано в одном месте, чтобы виджет
 * и отправка не разъехались: раньше формула была продублирована в двух файлах.
 */
export function isPromptRequired(
	model: Model | null,
	opts: { requiresVideo: boolean; isParametricAudio: boolean },
): boolean {
	if (model?.noPrompt) return false
	return !opts.requiresVideo && !opts.isParametricAudio
}

export function getModelConfig(selectedModel: Model | null): ModelConfig {
	const isVideo = selectedModel?.category === 'video'
	const requiresImage = selectedModel?.requiresImage ?? false
	const supportsImageInput = selectedModel?.supportsImageInput ?? false
	const requiresVideo = selectedModel?.requiresVideo ?? false
	const requiresTwoImages = selectedModel?.requiresTwoImages ?? false

	const maxImages = selectedModel?.maxImages ?? 1
	const supportsMultipleImages = maxImages > 1

	const showImageUpload =
		requiresImage || supportsImageInput || requiresTwoImages || supportsMultipleImages

	const paramHints: Record<string, { label?: string; description?: string }> =
		selectedModel?.paramHints || {}

	const hint = (key: string) => paramHints[key]?.description
	const hintLabel = (key: string, fallback: string) => paramHints[key]?.label || fallback

	return {
		isVideo,
		requiresImage,
		supportsImageInput,
		requiresVideo,
		requiresTwoImages,
		supportsMultipleImages,
		showImageUpload,
		maxImages,
		paramHints,
		hint,
		hintLabel,
	}
}

export const VERSION_OPTIONS = [
	{ label: 'v7', value: '7' },
	{ label: 'v6.1', value: '6.1' },
	{ label: 'v6', value: '6' },
	{ label: 'v5.2', value: '5.2' },
	{ label: 'Niji 6', value: 'niji6' },
]

export const SPEED_OPTIONS = [
	{ label: 'Turbo', value: SPEED.TURBO },
	{ label: 'Fast', value: SPEED.FAST },
	{ label: 'Relaxed', value: SPEED.RELAXED },
]

export const SAFETY_FILTER_OPTIONS = [
	{ label: 'Минимальный', value: SAFETY_FILTER.LOW },
	{ label: 'Средний', value: SAFETY_FILTER.MEDIUM },
	{ label: 'Строгий', value: SAFETY_FILTER.HIGH },
]

export function getFormatOptions(supportsOutputFormat?: boolean) {
	return [
		{ label: 'JPG', value: 'jpg' },
		{ label: 'PNG', value: 'png' },
		...(supportsOutputFormat ? [{ label: 'WebP', value: 'webp' }] : []),
	]
}
